import { onMounted } from "vue";

/**
 * Run `callback` after the overlay containing the list has committed its open
 * layout. The list mounts in the same tick its popup/bottom-sheet opens, so the
 * first animation frame still precedes the top-layer popover's Floating-UI
 * positioning (and the sheet's height): the scroll container would measure 0 and
 * scrollIntoView would no-op. The second frame runs after that layout is painted.
 *
 * @param {() => void} callback
 */
function runAfterOverlayLayout(callback) {
  requestAnimationFrame(() => requestAnimationFrame(callback));
}

/**
 * Reveal the active row once when the list's dropdown/menu opens (opt-in). The
 * scroll is deferred until the containing overlay has laid out; `block: "nearest"`
 * no-ops when the row is already visible, so it only scrolls a selection that
 * sits below the fold (e.g. inside an auto-expanded accordion branch).
 *
 * @param {import('vue').Ref<HTMLElement | null>} listRef - the scroll container ref.
 * @param {() => boolean} enabled - getter for the opt-in flag (resolved at mount).
 */
export function useScrollSelectedIntoView(listRef, enabled) {
  /** Scroll the active row into view, if there is one. */
  function scrollSelectedRowIntoView() {
    const active = listRef.value?.querySelector("sp-list-item[active]");
    active?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
  }

  /** On mount, reveal the selected row when the opt-in flag is set. */
  function scrollSelectedRowIntoViewOnOpen() {
    if (!enabled()) {
      return;
    }
    runAfterOverlayLayout(scrollSelectedRowIntoView);
  }

  onMounted(scrollSelectedRowIntoViewOnOpen);
}
